import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentGroupService } from '@shared/services/agentGroupService';
import { api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Button, Input } from '@shared/components/ui';
import { Plus, Trash2, Layers, Server, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function AgentGroups() {
  const { t } = useTranslation('agents');
  const { showConfirm } = useDialog();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const createDialogRef = useRef<HTMLDivElement>(null);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const {
    data: groups,
    isLoading: groupsLoading,
    isError: groupsError,
    refetch: refetchGroups,
  } = useQuery({
    queryKey: ['agentGroups'],
    queryFn: agentGroupService.listGroups,
  });

  const {
    data: agents,
    isLoading: agentsLoading,
    isError: agentsError,
    refetch: refetchAgents,
  } = useQuery({
    queryKey: ['agents'],
    queryFn: async () => (await api.get('/agents')).data,
  });

  const createGroupMutation = useMutation({
    mutationFn: () =>
      agentGroupService.createGroup({
        name: groupName,
        description: groupDescription,
        agent_ids: selectedAgents,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentGroups'] });
      setIsCreateModalOpen(false);
      resetForm();
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => agentGroupService.deleteGroup(groupId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agentGroups'] }),
  });

  const resetForm = () => {
    setGroupName('');
    setGroupDescription('');
    setSelectedAgents([]);
  };

  const groupList = groups ?? [];

  useEffect(() => {
    if (!isCreateModalOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    createDialogRef.current?.querySelector<HTMLInputElement>('#agent-group-name')?.focus();

    const handleDialogKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsCreateModalOpen(false);
        return;
      }

      if (event.key !== 'Tab' || !createDialogRef.current) return;

      const focusableElements = Array.from(
        createDialogRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.getClientRects().length > 0);

      if (focusableElements.length === 0) {
        event.preventDefault();
        createDialogRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      if (previouslyFocusedElement?.isConnected) previouslyFocusedElement.focus();
    };
  }, [isCreateModalOpen]);

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('groups.title')}</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('groups.subtitle')}
            </p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)} size="lg" className="shadow-brand-200">
            <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> {t('groups.create')}
          </Button>
        </div>

        {deleteGroupMutation.isError && (
          <p
            role="alert"
            className="text-sm font-semibold text-state-danger-light dark:text-state-danger-dark"
          >
            {t('groups.deleteError')}
          </p>
        )}

        {groupsLoading ? (
          <div className="py-20 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          </div>
        ) : groupsError ? (
          <Card className="py-16 text-center">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('groups.loadError')}
            </h3>
            <Button variant="outline" className="mt-4 min-h-11" onClick={() => refetchGroups()}>
              {t('groups.retry')}
            </Button>
          </Card>
        ) : groupList.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <Layers size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('groups.empty')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2">
              {t('groups.emptyDescription')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupList.map((group) => (
              <Card key={group.id} className="p-6 group hover:border-brand-200 transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-brand-600 dark:text-brand-400">
                      <Layers size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                        {group.name}
                      </h3>
                      <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-wider">
                        {t('groups.agentCount', { count: group.agent_count })}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('groups.deleteNamed', { name: group.name })}
                    onClick={async () => {
                      const confirmed = await showConfirm(
                        t('groups.deleteConfirmTitle'),
                        t('groups.confirmDeleteMessage', { name: group.name }),
                        'warning'
                      );
                      if (confirmed) deleteGroupMutation.mutate(group.id);
                    }}
                    className="min-h-11 min-w-11 text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>

                {group.description && (
                  <p className="text-sm text-text-muted-light dark:text-text-muted-dark mb-4 line-clamp-2">
                    {group.description}
                  </p>
                )}

                <div className="pt-4 border-t border-surface-border-light dark:border-surface-border-dark flex items-center text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                  <Server size={14} className="mr-2" /> {t('groups.managedResource')}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {isCreateModalOpen && (
          <div
            ref={createDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent-group-create-title"
            tabIndex={-1}
            className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
          >
            <Card className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto shadow-elevated">
              <div className="p-5 space-y-6 sm:p-8">
                <div className="flex justify-between items-center">
                  <h2
                    id="agent-group-create-title"
                    className="text-2xl font-black text-slate-900 dark:text-white tracking-tight"
                  >
                    {t('groups.newGroup')}
                  </h2>
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    aria-label={t('groups.close')}
                    className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <X className="text-text-subtle-light dark:text-text-subtle-dark" />
                  </button>
                </div>

                <div className="space-y-4">
                  <Input
                    id="agent-group-name"
                    label={t('groups.name')}
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder={t('groups.namePlaceholder')}
                    required
                  />
                  <div>
                    <label
                      htmlFor="agent-group-description"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('groups.description')}
                    </label>
                    <textarea
                      id="agent-group-description"
                      className="app-field-control min-h-24 w-full resize-y px-4 py-3"
                      value={groupDescription}
                      onChange={(e) => setGroupDescription(e.target.value)}
                      placeholder={t('groups.descriptionPlaceholder')}
                    />
                  </div>

                  <div role="group" aria-labelledby="agent-group-select-agents-label">
                    <label
                      id="agent-group-select-agents-label"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('groups.selectAgents')}
                    </label>
                    <div className="border border-surface-border-light dark:border-surface-border-dark rounded-xl max-h-48 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                      {agentsLoading ? (
                        <p className="py-4 text-center text-sm text-text-subtle-light dark:text-text-subtle-dark">
                          {t('groups.loadingAgents')}
                        </p>
                      ) : agentsError ? (
                        <div className="py-4 text-center">
                          <p
                            role="alert"
                            className="text-sm text-state-danger-light dark:text-state-danger-dark"
                          >
                            {t('groups.agentsLoadError')}
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-3 min-h-11"
                            onClick={() => void refetchAgents()}
                          >
                            {t('groups.retryAgents')}
                          </Button>
                        </div>
                      ) : (
                        agents?.map((agent: any) => (
                          <label
                            key={agent.id}
                            className="flex min-h-11 items-center px-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-lg cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              className="mr-3 h-5 w-5 rounded border-border-strong-light dark:border-border-strong-dark text-brand-600 dark:text-brand-400 focus:ring-brand-500"
                              checked={selectedAgents.includes(agent.id)}
                              onChange={() => {
                                setSelectedAgents((prev) =>
                                  prev.includes(agent.id)
                                    ? prev.filter((id) => id !== agent.id)
                                    : [...prev, agent.id]
                                );
                              }}
                            />
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {agent.name}
                            </span>
                          </label>
                        ))
                      )}
                      {!agentsLoading && !agentsError && !agents?.length && (
                        <p className="text-center text-sm text-text-subtle-light dark:text-text-subtle-dark py-4">
                          {t('groups.noAgentsAvailable')}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-right text-text-subtle-light dark:text-text-subtle-dark mt-2 font-bold">
                      {t('groups.selectedCount', { count: selectedAgents.length })}
                    </p>
                  </div>
                </div>

                {createGroupMutation.isError && (
                  <p
                    role="alert"
                    className="text-sm font-semibold text-state-danger-light dark:text-state-danger-dark"
                  >
                    {t('groups.createError')}
                  </p>
                )}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="secondary"
                    className="min-h-11 flex-1"
                    onClick={() => setIsCreateModalOpen(false)}
                  >
                    {t('groups.cancel')}
                  </Button>
                  <Button
                    className="min-h-11 flex-1"
                    onClick={() => createGroupMutation.mutate()}
                    disabled={!groupName.trim() || createGroupMutation.isPending}
                    isLoading={createGroupMutation.isPending}
                  >
                    {t('groups.create')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
